import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";

import { SearchFilterAttributeModel } from "../../../models/search-filter-attribute.model";
import { SearchFilterMetadataModel } from "../../../models/search-filter-metadata.model";
import { DirectorySearchForm } from "../../forms/directory-search.form";
import { AggregationGroupModel } from "../../models/aggregation-group.model";
import { AggregationItemModel } from "../../models/aggregation-item.model";
import { SearchTabConfigModel } from "../../models/search-tab-config.model";

// declare function externalResetSearchFormFn(): any;

export interface DisplayTag {
    value: string;
    title: string;
}

@Component({
    selector: "pcg-ng-directory-search-filters",
    templateUrl: "./directory-search-filters.component.html"
})
export class DirectorySearchFiltersComponent implements OnInit {
    @Input() public searchConfig: SearchTabConfigModel;
    @Input() public searchForm: DirectorySearchForm;
    @Input() public datesSpan$: BehaviorSubject<{ startDate: string, endDate: string, }>;
    @Input() public isMapVisible: boolean;
    @Input() public postcodeControl: FormControl;
    @Input() public distanceControl: FormControl;

    @Output() public searchButtonClicked = new EventEmitter<Event>();
    @Output() public toggleMapButtonClicked = new EventEmitter<Event>();
    @Output() public skipToResults = new EventEmitter<Event>();

    public localPostcodeControl: FormControl;
    public localDistanceControl: FormControl;

    public expandedCategoryIds = Array<string>();

    public selectedDisplayTags: Array<DisplayTag> = [];

    public categoriesFacet: AggregationGroupModel;
    public eventsCategoriesFacet: AggregationItemModel;

    public isFilterSectionVisible: boolean = false;

    private categoriesFilter: SearchFilterMetadataModel;
    private formSubscription: Subscription;

    private static mapFilterToAggregationGroup(filter: SearchFilterMetadataModel, value: Array<string> = []): AggregationGroupModel {
        const aggregation = {
            expanded: true,
            title: filter.caption,
            name: filter.alias,
            items: filter.attributes
                ? filter.attributes.map(attribute => DirectorySearchFiltersComponent.mapAttributeToAggregationItem(attribute, value))
                : []
        } as AggregationGroupModel;

        aggregation.items.forEach((category) => {
            category.expanded = (category.subcategories && category.subcategories.some(subcategory => subcategory.expanded || subcategory.selected));
        });

        return aggregation;
    }

    private static mapAttributeToAggregationItem(attribute: SearchFilterAttributeModel, value: Array<string> = []): AggregationItemModel {
        const aggregation = {
            selected: (value.indexOf(attribute.id) > -1),
            expanded: false,
            title: attribute.name,
            value: attribute.id,
            count: 0,
            subcategories: attribute.nestedAttributes
                ? attribute.nestedAttributes.map(nestedAttribute => DirectorySearchFiltersComponent.mapAttributeToAggregationItem(nestedAttribute, value))
                : []
        } as AggregationItemModel;

        aggregation.subcategories.forEach((category) => {
            category.expanded = (category.subcategories && category.subcategories.some(subcategory => subcategory.expanded || subcategory.selected));
        });

        return aggregation;
    }

    public ngOnInit(): void {
        if (this.searchForm && this.searchForm.topLevelCategoryIds) {
            this.expandedCategoryIds = this.searchForm.topLevelCategoryIds.value || [];
        }

        if (this.searchConfig && this.searchConfig.searchFilters) {
            this.categoriesFilter = this.searchConfig.searchFilters.find(item => item.alias === "categories");
        }

        this.localPostcodeControl = new FormControl(this.postcodeControl ? this.postcodeControl.value : "");
        this.localDistanceControl = new FormControl(this.distanceControl ? this.distanceControl.value : "1609");

        this.updateFacets();

        if (this.searchForm && this.searchForm.onFormChanged) {
            this.formSubscription = this.searchForm.onFormChanged.subscribe(() => {
                if (this.searchForm.topLevelCategoryIds) {
                    this.expandedCategoryIds = this.searchForm.topLevelCategoryIds.value || [];
                }

                this.updateFacets();
            });
        }
    }

    public ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    public onResetButtonClicked(event: Event): void {
        event.preventDefault();

        this.searchForm.categories.setValue([], { emitEvent: false });
        this.searchForm.topLevelCategoryIds.setValue([], { emitEvent: false });

        this.searchForm.instance.patchValue({
            pageNumber: 1,
            postcode: "",
            distance: "1609",
            sortOption: "Rank;asc",
            categories: [],
            topLevelCategoryIds: []
        }, { emitEvent: false });

        this.expandedCategoryIds = [];
        this.localPostcodeControl.setValue("");
        this.localDistanceControl.setValue("1609");

        this.updateFacets();

        if (typeof this.searchForm.submit === "function") {
            this.searchForm.submit();
        }
    }

    public categoryCheckboxChange(event: Event): void {
        const element = event.target as HTMLInputElement;
        const value = element.value;

        if (element.checked) {
            this.expandedCategoryIds = [value];
        } else {
            this.expandedCategoryIds = this.expandedCategoryIds.filter(item => item !== value);
        }

        const incomingFacet = this.buildAggregatedCategory(this.categoriesFacet.items);
        const validIds = this.collectAllIds(incomingFacet?.subcategories ?? []);

        const currentCategories: Array<string> = this.searchForm.categories.value || [];
        const retainedCategories = currentCategories.filter(id => validIds.includes(id));

        this.searchForm.categories.setValue(retainedCategories, { emitEvent: false });
        this.searchForm.topLevelCategoryIds.setValue(this.expandedCategoryIds);
    }

    public isCategoryChecked(categoryId: string): boolean {
        return this.expandedCategoryIds.includes(categoryId);
    }

    public onToggleFilterSection(event: Event): void {
        event.preventDefault();
        this.isFilterSectionVisible = !this.isFilterSectionVisible;
    }

    public onSearchButtonClicked(event: Event): void {
        event.preventDefault();

        if (this.postcodeControl) {
            this.postcodeControl.setValue(this.localPostcodeControl.value, { emitEvent: false });
        }
        if (this.distanceControl) {
            this.distanceControl.setValue(this.localDistanceControl.value, { emitEvent: false });
        }

        this.searchForm.instance.patchValue({
            postcode: this.localPostcodeControl.value,
            distance: this.localDistanceControl.value,
            pageNumber: 1
        }, { emitEvent: false });

        this.searchButtonClicked.emit(event);

        if (this.searchForm && typeof this.searchForm.submit === "function") {
            this.searchForm.submit();
        }
    }

    public onToggleMapButtonClicked(event: Event): void {
        this.toggleMapButtonClicked.emit(event);
    }

    public GetSelectedFiltersCount(): number {
        if (!this.eventsCategoriesFacet?.subcategories) return 0;

        let selectedValuesCount = 0;
        const eventCategories = this.eventsCategoriesFacet.subcategories;

        eventCategories.forEach((category) => {
            selectedValuesCount += this.GetSelectedFiltersCountInGroup(category);
        });

        return selectedValuesCount;
    }

    public GetSelectedFiltersCountInGroup(category: AggregationItemModel): number {
        let selectedValuesCount = 0;

        category.subcategories?.forEach((subcategory) => {
            if (subcategory.selected) {
                selectedValuesCount += 1;
            }

            if (subcategory.subcategories?.length) {
                selectedValuesCount += this.GetSelectedFiltersCountInGroup(subcategory);
            }
        });

        return selectedValuesCount;
    }

    public removeTag(value: string): void {
        if (this.expandedCategoryIds.includes(value)) {
            this.expandedCategoryIds = this.expandedCategoryIds.filter(item => item !== value);
        } else {
            const currentCategories = this.searchForm.categories.value || [];
            const newCategories = currentCategories.filter((id: string) => id !== value);
            this.searchForm.categories.setValue(newCategories);
        }
        this.searchForm.topLevelCategoryIds.setValue(this.expandedCategoryIds);

        this.onSearchButtonClicked(new Event("submit"));
    }

    public onSkipToResults(event: Event): void {
        this.skipToResults.emit(event);
    }

    private updateFacets(): void {
        if (this.categoriesFilter && this.searchForm) {
            this.categoriesFacet = DirectorySearchFiltersComponent.mapFilterToAggregationGroup(
                this.categoriesFilter,
                this.searchForm.categories.value || []
            );
        }
        if (this.categoriesFacet?.items?.length) {
            this.eventsCategoriesFacet = this.buildAggregatedCategory(this.categoriesFacet.items);
        }

        this.updateSelectedDisplayTags();
    }

    private updateSelectedDisplayTags(): void {
        const source = this.eventsCategoriesFacet ? [this.eventsCategoriesFacet] : (this.categoriesFacet?.items || []);
        this.selectedDisplayTags = this.collectSelectedTags(source);
    }

    private collectSelectedTags(items: Array<AggregationItemModel>): Array<DisplayTag> {
        let tags: Array<DisplayTag> = [];
        items?.forEach((item) => {
            if (item.selected) {
                tags.push({ value: item.value, title: item.title });
            }
            if (item.subcategories?.length) {
                tags = tags.concat(this.collectSelectedTags(item.subcategories));
            }
        });
        return tags;
    }

    private buildAggregatedCategory(items: Array<AggregationItemModel>): AggregationItemModel | null {
        const matched = this.expandedCategoryIds.length !== 0
            ? items.filter(item => this.expandedCategoryIds.includes(item.value))
            : this.searchConfig.defaultSearchFilterDirectoryTemplateId
                ? items.filter(item => item.value === this.searchConfig.defaultSearchFilterDirectoryTemplateId)
                : [];

        if (matched.length === 0) {
            return null;
        }

        const allCategories = matched.flatMap(item => item.subcategories);
        const mergedCategories = this.mergeCategories(allCategories);

        const aggregation: AggregationItemModel = {
            selected: false,
            expanded: true,
            title: "Events Category",
            value: matched.map(m => m.value).join("_"),
            count: 0,
            subcategories: mergedCategories
        };

        return aggregation;
    }

    private mergeCategories(categories: Array<AggregationItemModel>): Array<AggregationItemModel> {
        const map = new Map<string, AggregationItemModel>();

        for (const category of categories) {
            const existing = map.get(category.value);

            if (!existing) {
                map.set(category.value, {
                    value: category.value,
                    title: category.title,
                    count: category.count,
                    subcategories: category.subcategories ? this.mergeCategories(category.subcategories) : [],
                    selected: category.selected,
                    expanded: category.expanded
                });
            } else {
                existing.count += category.count;

                existing.subcategories = this.mergeCategories([
                    ...(existing.subcategories ?? []),
                    ...(category.subcategories ?? [])
                ]);

                existing.selected = existing.selected || category.selected;
                existing.expanded = existing.expanded || category.expanded;
            }
        }

        return Array.from(map.values());
    }

    private collectAllIds(items: Array<AggregationItemModel>): Array<string> {
        let ids: Array<string> = [];
        items?.forEach((item) => {
            ids.push(item.value);
            if (item.subcategories?.length) {
                ids = ids.concat(this.collectAllIds(item.subcategories));
            }
        });
        return ids;
    }
}
